import { Injectable, Signal } from '@angular/core';
import { CallState, CallSession, CallDisposition, CallDirection } from './call-types';
import { CallStateStore } from './call-state.store';
import { CallSessionService } from './call-session.service';
import { CallEventsService } from './call-events.service';
import { CallTimerService } from './call-timer.service';
import { CallErrorHandler } from './call-error-handler.service';

/**
 * Service for managing call lifecycle and state transitions
 * Handles initialization, termination, and state machine logic
 *
 * Target: ~280 lines (under 300 line requirement)
 */

// State transition matrix - defines valid state transitions
const VALID_TRANSITIONS: Record<CallState, CallState[]> = {
  'idle': ['dialing', 'ringing'],
  'dialing': ['ringing', 'connecting', 'connected', 'failed', 'ended', 'ending'],
  'ringing': ['connecting', 'connected', 'ended', 'failed', 'ending'],
  'connecting': ['connected', 'failed', 'ended', 'ending'],
  'connected': ['held', 'ending', 'ended'],
  'holding': ['held', 'ended'],
  'held': ['connected', 'ending', 'ended'],
  'ending': ['ended', 'post-call', 'idle'],
  'ended': ['post-call', 'idle', 'dialing'],
  'post-call': ['idle', 'dialing'],
  'failed': ['post-call', 'idle', 'dialing']
};

// Terminal states that should not be overridden
const TERMINAL_STATES: CallState[] = ['ended', 'failed', 'post-call', 'idle'];

/**
 * Parameters for incoming call registration
 */
export interface IncomingCallParams {
  phoneNumber: string;
  telnyxCallId: string;
  leadId?: number;
  callerName?: string;
  leadInfo?: any;
  dbCallId?: number;  // Database call ID from custom headers for disposition API
  isLeadAlert?: boolean;  // True if this is a lead alert call
  alertSessionId?: string;  // Lead alert session ID from backend
}

@Injectable({
  providedIn: 'root'
})
export class CallLifecycleService {
  // State transition tracking
  private lastStateTransition = 0;
  private isTerminating = false;

  constructor(
    private store: CallStateStore,
    private sessionService: CallSessionService,
    private eventsService: CallEventsService,
    private timerService: CallTimerService,
    private errorHandler: CallErrorHandler
  ) {}

  // Public getters for state (signals from store)
  get currentState(): Signal<CallState> {
    return this.store.callState;
  }

  get isTerminalState(): Signal<boolean> {
    return this.store.isTerminalState;
  }

  /**
   * Check if transition to target state is valid
   */
  canTransitionTo(targetState: CallState): boolean {
    const currentState = this.store.getCurrentState();
    const isValid = (VALID_TRANSITIONS[currentState] || []).includes(targetState);
    // Special case: allow incoming calls from terminal states
    return isValid || (targetState === 'ringing' && TERMINAL_STATES.includes(currentState));
  }

  /**
   * Transition to a new state with validation and debouncing
   */
  transitionTo(newState: CallState, session?: CallSession): boolean {
    const currentState = this.store.getCurrentState();
    const now = Date.now();


    // Debounce: prevent rapid successive transitions
    if (now - this.lastStateTransition < 100 && currentState === newState) return false;
    // Prevent transitions during termination (except to terminal states)
    if (this.isTerminating && !TERMINAL_STATES.includes(newState)) return false;
    // Prevent duplicate state transitions
    if (currentState === newState) return false;
    // Validate transition
    if (!this.canTransitionTo(newState)) {
      console.warn(`Invalid state transition: ${currentState} -> ${newState}`);
      return false;
    }

    // Execute transition
    this.lastStateTransition = now;
    this.store.setState(newState);

    // Update session if provided
    if (session) {
      this.store.setActiveCall(session);
      this.sessionService.updateSession(session.id, { state: newState });
    }

    this.handleStateSpecificLogic(newState, currentState);
    this.eventsService.emitStateChange(currentState, newState, session);
    return true;
  }

  /**
   * Initialize an outbound call
   */
  async initializeOutbound(phoneNumber: string, leadId?: number): Promise<CallSession> {
    if (!this.canTransitionTo('dialing')) {
      const error = this.errorHandler.processError(
        new Error(`Cannot initiate call from state: ${this.store.getCurrentState()}`),
        'INVALID_STATE_TRANSITION'
      );
      this.eventsService.emitError(error);
      throw new Error(error.message);
    }

    try {
      this.store.setProcessing(true);
      const session = this.sessionService.createSession({
        direction: 'outbound', phoneNumber, leadId, state: 'dialing'
      });

      this.transitionTo('dialing', session);
      this.timerService.startCallTimer(session.id, (d) => this.store.setDuration(d));
      this.timerService.startCleanupTimer(300000, () => {
        if (!TERMINAL_STATES.includes(this.store.getCurrentState())) this.forceReset();
      });
      this.eventsService.emitCallStarted(session);
      return session;
    } finally {
      this.store.setProcessing(false);
    }
  }

  /**
   * Register an incoming call
   */
  registerInbound(params: IncomingCallParams): CallSession {
    const currentState = this.store.getCurrentState();

    // Force reset if needed for incoming calls
    if (!this.canTransitionTo('ringing') && currentState !== 'idle') this.forceReset();

    if (!this.canTransitionTo('ringing')) {
      const error = this.errorHandler.processError(
        new Error(`Cannot register incoming call from state: ${currentState}`),
        'INVALID_STATE_TRANSITION'
      );
      this.eventsService.emitError(error);
      throw new Error(error.message);
    }

    this.sessionService.clearStale();
    const session = this.sessionService.createSession({
      direction: 'inbound',
      phoneNumber: params.phoneNumber,
      leadId: params.leadId,
      state: 'ringing',
      telnyxCallId: params.telnyxCallId,
      customerCallId: params.telnyxCallId
    });

    if (params.callerName) {
      this.sessionService.updateSession(session.id, { notes: `Caller: ${params.callerName}` });
    }

    // Attach leadInfo to session for modal access (non-standard property)
    if (params.leadInfo) {
      (session as any).leadInfo = params.leadInfo;
    }

    // Attach dbCallId to session for disposition API (from custom headers)
    // Also set backendCallId to the same value for consistency with outbound calls
    if (params.dbCallId) {
      (session as any).dbCallId = params.dbCallId;
      session.backendCallId = params.dbCallId;
    }

    // Attach lead alert properties to session for routing
    if (params.isLeadAlert) {
      (session as any).isLeadAlert = params.isLeadAlert;
    }
    if (params.alertSessionId) {
      (session as any).alertSessionId = params.alertSessionId;
    }

    this.transitionTo('ringing', session);
    this.timerService.startCallTimer(session.id, (d) => this.store.setDuration(d));
    this.eventsService.emitCallStarted(session);
    return session;
  }

  /**
   * Terminate a call with disposition
   */
  terminateCall(session: CallSession, disposition: CallDisposition): void {
    if (!this.canTransitionTo('ending')) {
      const error = this.errorHandler.processError(
        new Error(`Cannot end call from state: ${this.store.getCurrentState()}`),
        'INVALID_STATE_TRANSITION'
      );
      this.eventsService.emitError(error);
      return;
    }

    try {
      this.isTerminating = true;
      this.transitionTo('ending');

      const endTime = new Date();
      const duration = Math.floor((endTime.getTime() - session.startTime.getTime()) / 1000);

      this.sessionService.updateSession(session.id, {
        endTime, duration, disposition, state: 'ended'
      });

      this.timerService.stopCallTimer(session.id);
      this.transitionTo('ended');

      const finalizedSession = this.sessionService.finalizeSession(session.id, 'ended');
      if (finalizedSession) {
        this.eventsService.emitCallEnded(finalizedSession, disposition);
      }

      this.performCleanup(session.id);
    } finally {
      this.isTerminating = false;
    }
  }

  /**
   * Force reset to idle state
   */
  public forceReset(): void {
    this.isTerminating = true;
    this.timerService.clearAll();

    const currentState = this.store.getCurrentState();
    const activeCall = this.store.getActiveCall();

    if (activeCall && !activeCall.endTime) {
      this.sessionService.finalizeSession(activeCall.id, 'ended');
    }

    this.store.reset();
    this.sessionService.clearAll();
    this.eventsService.emitStateChange(currentState, 'idle');

    setTimeout(() => { this.isTerminating = false; }, 500);
  }

  /**
   * Handle state-specific logic
   */
  private handleStateSpecificLogic(newState: CallState, previousState: CallState): void {
    if (newState === 'ended' || newState === 'failed') {
      setTimeout(() => {
        if (this.store.getCurrentState() === newState && !this.isTerminating) {
          this.transitionTo('post-call');
          this.startPostCallTimer();
        }
      }, 50);
    } else if (newState === 'idle') {
      this.performIdleCleanup();
    } else if (newState === 'post-call' && previousState !== 'post-call') {
      this.timerService.clearPostCallTimer();
      this.startPostCallTimer();
    }

    if (previousState === 'post-call' && newState !== 'post-call') {
      this.timerService.clearPostCallTimer();
    }
  }

  /**
   * Start post-call timer
   */
  private startPostCallTimer(): void {
    this.timerService.startPostCallTimer(300, () => {
      if (this.store.getCurrentState() === 'post-call' && !this.isTerminating) {
        this.transitionTo('idle');
      }
    });
  }

  /**
   * Perform cleanup for a call session
   */
  private performCleanup(sessionId: string): void {
    this.timerService.stopCallTimer(sessionId);
    this.sessionService.removeSession(sessionId);
    this.store.setProcessing(false);
    this.timerService.clearCleanupTimer();
  }

  /**
   * Perform cleanup when entering idle state
   */
  private performIdleCleanup(): void {
    this.store.setActiveCall(null);
    this.store.setDuration(0);
    this.timerService.clearAll();
  }
}
